import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircleOutline,
  OpenInNew,
  Refresh,
} from '@mui/icons-material';
import DocxDropZone from './DocxDropZone';
import DocxMetadataPreview from './DocxMetadataPreview';
import useDocxUpload from './hooks/useDocxUpload';

/**
 * DOCX Upload component for the production process dashboard.
 *
 * Flow:
 * 1. User drops/selects a DOCX file
 * 2. Filename is sent to backend to lookup metadata
 * 3. User confirms the metadata match
 * 4. File is uploaded to S3
 * 5. Kimai project is created (simulated)
 */
function DocxUpload() {
  const [file, setFile] = useState(null);
  const [metadataResponse, setMetadataResponse] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);

  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  const {
    lookupMetadata,
    uploadFile,
    isLookingUp,
    isUploading,
  } = useDocxUpload(apiBaseUrl);

  const handleFileSelect = useCallback(async (selectedFile) => {
    setFile(selectedFile);
    setMetadataResponse(null);
    setUploadResult(null);
    setError(null);

    try {
      const response = await lookupMetadata(selectedFile.name);
      setMetadataResponse(response);
    } catch (err) {
      setError(`Failed to lookup metadata: ${err.message}`);
    }
  }, [lookupMetadata]);

  const handleConfirm = useCallback(async () => {
    if (!file || !metadataResponse?.metadata?.ean) {
      setError('Cannot upload: missing file or metadata');
      return;
    }

    try {
      const result = await uploadFile(
        file,
        metadataResponse.metadata.ean,
        metadataResponse.metadata.digital_asset_collection_key
      );
      setUploadResult(result);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    }
  }, [file, metadataResponse, uploadFile]);

  const handleReject = useCallback(() => {
    setFile(null);
    setMetadataResponse(null);
    setUploadResult(null);
    setError(null);
  }, []);

  const handleReset = useCallback(() => {
    setFile(null);
    setMetadataResponse(null);
    setUploadResult(null);
    setError(null);
  }, []);

  return (
    <Box sx={{ p: 4, width: '90%', mx: 'auto', maxWidth: 900 }}>
      <Typography variant="h4" gutterBottom>
        DOCX Upload
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload DOCX files to start the production process. The system will match the file
        with existing metadata based on the filename.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {uploadResult ? (
        <Box>
          {/* Success header */}
          <Alert
            severity="success"
            icon={<CheckCircleOutline fontSize="large" />}
            sx={{ mb: 3, alignItems: 'center' }}
          >
            <Typography variant="h6" fontWeight="bold">
              Upload successful
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {uploadResult.filename} has been uploaded and the Kimai project is ready.
            </Typography>
          </Alert>

          {/* Project details card */}
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 3,
              mb: 3,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="overline" color="text.secondary" gutterBottom>
              Kimai Project
            </Typography>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {uploadResult.kimai_project_name || uploadResult.kimai_project_id || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Status: {uploadResult.kimai_status}
            </Typography>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="contained"
              endIcon={<OpenInNew />}
              href="https://stechuhr.transcript-verlag.de/de/timesheet/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Start Time Tracking
            </Button>
            {metadataResponse?.metadata?.impression_id && (
              <Button
                variant="outlined"
                endIcon={<OpenInNew />}
                href={`http://192.168.135.68:8281/tmm/tenants/prod/#/productDetail/PRD/transcript%20Standard/${metadataResponse.metadata.impression_id}/D/952/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in TLM
              </Button>
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Secondary action */}
          <Button
            variant="text"
            startIcon={<Refresh />}
            onClick={handleReset}
          >
            Upload another file
          </Button>
        </Box>
      ) : (
        <>
          <DocxDropZone
            onFileSelect={handleFileSelect}
            selectedFile={file}
            isLoading={isLookingUp}
            disabled={isUploading}
          />

          {metadataResponse && (
            <DocxMetadataPreview
              response={metadataResponse}
              onConfirm={handleConfirm}
              onReject={handleReject}
              isUploading={isUploading}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default DocxUpload;
